import { from, Observable, of } from 'rxjs';
import { catchError, concatMap, timeout } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    LocalstorageHelper,
    LocalStorageService,
    LogService,
    OnlineService,
    SerializabledDataType,
    TIMEOUT_CACHE
} from '@nts/std/utility';
import { VersionInterface, VersionManager } from './version-manager';

export const VERSION_LOADER_STORAGE_KEY = 'version';

@Injectable({
    providedIn: 'root',
})
export class VersionLoader {
    constructor(
        private http: HttpClient,
        private onlineService: OnlineService,
        private localStorageService: LocalStorageService
    ) { }

    getVersion(): Observable<VersionInterface> {
        return from(this.localStorageService.init()).pipe(
            concatMap(() =>
                from(
                    LocalstorageHelper.getStorageItem(
                        VERSION_LOADER_STORAGE_KEY,
                        undefined,
                        false,
                        false
                    )
                )
            ),
            concatMap((storedVersion) => this.loadVersion(storedVersion)),
            concatMap((version) => from(this.persistAndSetVersion(version)))
        );
    }

    private loadVersion(
        storedVersion: SerializabledDataType
    ): Observable<VersionInterface> {
        if (this.onlineService.isOnline === false) {
            return of(this.toVersion(storedVersion));
        }

        const request$ = this.http.get<VersionInterface>('assets/std/version.json').pipe(
            catchError((e) => {
                LogService.warn('missing std version file!', e);
                return of(this.toVersion(storedVersion));
            })
        );

        if (storedVersion) {
            return request$.pipe(
                timeout({
                    first: TIMEOUT_CACHE,
                    with: () => of(this.toVersion(storedVersion)),
                })
            );
        }

        return request$;
    }

    private async persistAndSetVersion(
        version: VersionInterface
    ): Promise<VersionInterface> {
        await LocalstorageHelper.setStorageItem(
            VERSION_LOADER_STORAGE_KEY,
            version,
            undefined,
            false,
            false
        );

        VersionManager.Current.version = version;
        return version;
    }

    private toVersion(value: SerializabledDataType): VersionInterface {
        if (this.isVersionInterface(value)) {
            return value;
        }

        return {} as VersionInterface;
    }

    private isVersionInterface(value: unknown): value is VersionInterface {
        if (typeof value !== 'object' || value === null || Array.isArray(value)) {
            return false;
        }

        const candidate = value as Record<string, unknown>;

        return (
            typeof candidate['FrameworkJsVersion'] === 'string' &&
            typeof candidate['FrameworkJsInformativeVersion'] === 'string' &&
            typeof candidate['JsVersion'] === 'string' &&
            typeof candidate['JsInformativeVersion'] === 'string'
        );
    }
}
